import React, { useEffect, useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Modal, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { EquipmentCatalogDto, EquipmentCategory, EquipmentDifficultyLevel } from '@wellverse/shared-ts';
import { ExcelButtons } from '../../components/ExcelButtons';
import { API_BASE_URL, axiosInstance } from '../../providers/dataProvider';

const categoryColors: Record<string, string> = {
  [EquipmentCategory.FREE_WEIGHTS]: '#f5a623',
  [EquipmentCategory.MACHINE]: '#1890ff',
  [EquipmentCategory.CABLE]: '#722ed1',
  [EquipmentCategory.CARDIO]: '#eb2f96',
  [EquipmentCategory.BODYWEIGHT]: '#3ecf8e',
  [EquipmentCategory.ACCESSORIES]: '#898989',
};

const difficultyColors: Record<string, string> = {
  [EquipmentDifficultyLevel.BEGINNER]: '#3ecf8e',
  [EquipmentDifficultyLevel.INTERMEDIATE]: '#f5a623',
  [EquipmentDifficultyLevel.ADVANCED]: '#ff4d4f',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const EquipmentCatalogList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<EquipmentCategory[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<EquipmentDifficultyLevel[]>([]);
  const [selectedMuscles, setSelectedMuscles] = useState<string[]>([]);
  const [muscleOptions, setMuscleOptions] = useState<string[]>([]);
  const [enlargeImages, setEnlargeImages] = useState<{ name: string; urls: string[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get<string[]>(`${API_BASE_URL}/equipment-catalog/target-muscle-groups`)
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) setMuscleOptions(res.data);
      })
      .catch(() => {
        // Keep empty options; user can still type via tags mode.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { tableProps } = useTable<EquipmentCatalogDto>({
    resource: 'equipment-catalog',
    filters: {
      permanent: [
        { field: 'query', operator: 'contains', value: searchText },
        ...(selectedCategories.length
          ? [{ field: 'category', operator: 'in' as const, value: selectedCategories }]
          : []),
        ...(selectedDifficulties.length
          ? [{ field: 'difficultyLevel', operator: 'in' as const, value: selectedDifficulties }]
          : []),
        ...(selectedMuscles.length
          ? [{ field: 'targetMuscleGroups', operator: 'in' as const, value: selectedMuscles }]
          : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="equipment-catalog">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by name, brand, model..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
        <Select
          mode="multiple"
          placeholder="Filter by category"
          allowClear
          maxTagCount="responsive"
          style={{ minWidth: 200 }}
          value={selectedCategories}
          onChange={(value) => setSelectedCategories(value)}
          options={Object.values(EquipmentCategory).map((c) => ({ label: c, value: c }))}
        />
        <Select
          mode="multiple"
          placeholder="Filter by difficulty"
          allowClear
          maxTagCount="responsive"
          style={{ minWidth: 200 }}
          value={selectedDifficulties}
          onChange={(value) => setSelectedDifficulties(value)}
          options={Object.values(EquipmentDifficultyLevel).map((d) => ({ label: d, value: d }))}
        />
        <Select
          mode="tags"
          placeholder="Target muscles"
          allowClear
          showSearch
          maxTagCount="responsive"
          style={{ minWidth: 220 }}
          value={selectedMuscles}
          onChange={(value) => setSelectedMuscles(value)}
          options={muscleOptions.map((m) => ({ label: m, value: m }))}
        />
        <ExcelButtons resource="equipment-catalog" />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column
          dataIndex="imageUrls"
          title="Image"
          width={80}
          render={(urls: string[], record: EquipmentCatalogDto) => {
            const src = urls?.[0];
            if (!src) return <span style={{ color: '#bfbfbf' }}>—</span>;
            return (
              <img
                src={src}
                alt=""
                style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 4, background: '#fafafa', cursor: 'pointer' }}
                onClick={() => setEnlargeImages({ name: record.name, urls })}
              />
            );
          }}
        />
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column
          title="Translations"
          width={220}
          render={(_: any, record: EquipmentCatalogDto) => {
            const t = record.translations;
            if (!t || Object.keys(t).length === 0) return '—';
            return (
              <Space wrap size={4}>
                {Object.entries(t).map(([locale, data]) => (
                  <Tag key={locale} style={{ borderRadius: 9999, padding: '2px 8px', fontSize: 12 }}>
                    {locale}: {(data as { name?: string }).name || '—'}
                  </Tag>
                ))}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="brand"
          title="Brand / Model"
          render={(_: any, record: EquipmentCatalogDto) => (
            <span>{record.brand} {record.model}</span>
          )}
        />
        <Table.Column
          dataIndex="category"
          title="Category"
          render={(cat: EquipmentCategory) => (
            <Tag style={tagStyle(categoryColors[cat] || '#898989')}>{cat}</Tag>
          )}
        />
        <Table.Column
          dataIndex="difficultyLevel"
          title="Difficulty"
          render={(level: EquipmentDifficultyLevel) => (
            <Tag style={tagStyle(difficultyColors[level] || '#898989')}>{level}</Tag>
          )}
        />
        <Table.Column
          dataIndex="targetMuscleGroups"
          title="Target Muscles"
          render={(groups: string[]) => {
            if (!groups || groups.length === 0) return '—';
            const display = groups.slice(0, 2);
            const extra = groups.length - 2;
            return (
              <Space wrap size={4}>
                {display.map((g) => (
                  <Tag key={g} style={{ borderRadius: 9999, padding: '2px 8px' }}>{g}</Tag>
                ))}
                {extra > 0 && <span style={{ color: '#898989' }}>+{extra}</span>}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          render={(isActive: boolean) => (
            <Tag style={tagStyle(isActive ? '#3ecf8e' : '#898989')}>
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          )}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: any, record: EquipmentCatalogDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="equipment-catalog" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="equipment-catalog" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="equipment-catalog" />
            </Space>
          )}
        />
      </Table>
      <Modal
        open={!!enlargeImages}
        title={enlargeImages?.name}
        footer={null}
        onCancel={() => setEnlargeImages(null)}
        width={720}
      >
        <Space size={12} wrap>
          {enlargeImages?.urls.map((url, i) => (
            <img
              key={i}
              src={url}
              alt=""
              style={{ maxWidth: 320, maxHeight: 320, objectFit: 'contain', borderRadius: 4, background: '#fafafa' }}
            />
          ))}
        </Space>
      </Modal>
    </List>
  );
};
